import React, { useRef, useState } from 'react';
import {
  GlobalOutlined,
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  ExportOutlined,
  CopyOutlined,
  DesktopOutlined,
  TabletOutlined,
  MobileOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { useProxyGrant } from './use-proxy-grant';
import { AssetVersionItemDto } from '@api/Api';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import './WebsiteCard.less';

interface WebsiteCardProps {
  asset: AssetVersionItemDto & { metadata: { type: 'website' } };
}

// Sentinel flag the proxied page stamps on every outbound bridge
// envelope (see `web-api-patch.ts`'s "Parent-frame bridge" block). We
// filter on it because the iframe loads a third-party site that may
// post arbitrary messages of its own; without the flag we'd react to
// every cross-origin message that happens to share our `type` field.
const BRIDGE_FLAG = '__sw$proxyBridge';

type NavigationType = 'push' | 'replace' | 'reload' | 'traverse';

type ChildToParent =
  | {
      type: 'sw-proxy-navigate';
      url: string;
      state: unknown;
      navigationType: NavigationType;
      sameDoc: boolean;
      // The iframe didn't preventDefault — same-doc downgrades and
      // form submissions handle the navigation themselves; the
      // parent updates its stack but doesn't replay.
      letThrough?: boolean;
    }
  | { type: 'sw-proxy-loaded'; url: string }
  | { type: 'sw-proxy-traverse-request'; delta: number }
  | { type: 'sw-proxy-favicon'; href: string | null }
  | { type: 'sw-proxy-title'; title: string }
  | { type: 'service-worker-not-supported' }
  | { type: 'service-worker-install-failed' };

type ParentToChild =
  | { type: 'sw-proxy-load'; url: string }
  | { type: 'sw-proxy-traverse-same-doc'; url: string; state: unknown }
  | { type: 'sw-proxy-restore-state'; state: unknown }
  | { type: 'sw-proxy-stack-length'; length: number }
  | { type: 'sw-proxy-reload' }
  | { type: 'sw-proxy-clear-site-data' };

type HistoryEntry = { url: string; state: unknown; docId: number };

type HistoryState = {
  entries: HistoryEntry[];
  idx: number;
  // Document group of the entry the iframe currently displays. Each
  // cross-doc nav (or cross-doc traversal) mints a fresh `docId`;
  // same-doc operations (pushState, replaceState, hash) inherit it.
  // Traversal within the current docId stays in-document
  // (replaceState + synthetic popstate — SPA in-memory state
  // survives); traversal across docIds reloads via location.replace.
  currentDocId: number;
  nextDocId: number;
};

type Viewport = 'desktop' | 'tablet' | 'mobile';

// Pixel dimensions chosen to match common device viewports so
// responsive sites render their tablet/mobile layouts.
const VIEWPORT_SIZES: Record<Exclude<Viewport, 'desktop'>, number> = {
  tablet: 768,
  mobile: 390
};

function WebsiteCard({ asset }: WebsiteCardProps) {
  const { url, proxyUrl } = asset.metadata;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Proxy grant cookie: the backend gates every proxy-origin request
  // on a short-lived HMAC cookie minted in exchange for a reCAPTCHA
  // v3 token. Mint it before the iframe is rendered so the very
  // first request already carries the cookie; re-mint inside the
  // hook before expiry.
  const grant = useProxyGrant();
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState(url);
  const [pageTitle, setPageTitle] = useState<string>(asset.name);
  const [error, setError] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Viewport>('desktop');
  // Session-history stack — the single source of truth for iframe
  // back/forward. Lives in the viewer (not the iframe) so a
  // full-page navigation inside the iframe doesn't reset state.
  //
  // Architecture (see also `web-api-patch.ts`'s "Parent-frame
  // bridge" block):
  //   - The iframe's `pushState` is downgraded to `replaceState`
  //     internally and reported here as a 'push' intent. Iframe's
  //     native session history therefore stays at length 1 per real
  //     document; the host browser's back button can never rewind
  //     iframe state.
  //   - Cross-doc navs (link clicks, location.href, etc.) are
  //     cancelled by the iframe and replayed by us via
  //     `sw-proxy-load`, which the iframe performs as
  //     `location.replace`.
  //   - `history.back/forward/go` inside the iframe is intercepted
  //     and routed here as `sw-proxy-traverse-request`; we fold it
  //     against this stack the same way as the toolbar buttons.
  //   - State is stored per entry. Same-doc traversal sends state
  //     back via `sw-proxy-traverse-same-doc` (replaceState + sync
  //     popstate); cross-doc traversal sends it back via
  //     `sw-proxy-restore-state` post-load.
  //
  // `historyRef` mirrors `history` synchronously so the message
  // handler (registered once) reads fresh values without
  // re-subscribing per navigation. Always go through `updateHistory`
  // rather than `setHistoryState` directly so the ref stays in sync
  // and the iframe's `history.length` mirror is updated.
  const [history, setHistoryState] = React.useState<HistoryState>({
    entries: [],
    idx: -1,
    currentDocId: 0,
    nextDocId: 1
  });
  const historyRef = React.useRef(history);

  // Which toolbar button is currently waiting on a cross-doc load to
  // finish, so we can swap its icon for a spinner. `null` when the
  // page is idle, or when an in-flight load was initiated by the
  // iframe itself (link click / form submit / page-script nav) — in
  // that case `isLoading` is still true and the reload button stands
  // in as the indicator.
  const [loadingButton, setLoadingButton] = useState<
    'back' | 'forward' | 'reload' | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  const sendCommand = React.useCallback((command: ParentToChild) => {
    const cw = iframeRef.current?.contentWindow;
    // Target origin is `*` because the iframe lives at the proxy
    // origin and we don't tightly couple the viewer to a specific
    // proxy hostname here. The proxied page's listener filters
    // `event.source === parentWin` on the other side, which is the
    // pairing that actually matters for trust.
    cw?.postMessage(command, '*');
  }, []);

  const updateHistory = React.useCallback(
    (next: HistoryState) => {
      const lengthChanged =
        next.entries.length !== historyRef.current.entries.length;
      historyRef.current = next;
      setHistoryState(next);
      if (lengthChanged) {
        sendCommand({
          type: 'sw-proxy-stack-length',
          length: Math.max(1, next.entries.length)
        });
      }
    },
    [sendCommand]
  );

  // Apply a traversal to `newIdx`. Decides same-doc vs cross-doc
  // based on docId and dispatches the right command. Used by toolbar
  // buttons and by `sw-proxy-traverse-request` from the iframe.
  // `initiator` names the toolbar button driving the traversal (if
  // any) so the spinner can render on the right control; we only set
  // it for cross-doc loads since same-doc traversal completes
  // synchronously and won't emit a `sw-proxy-loaded` to clear it.
  const traverseTo = React.useCallback(
    (newIdx: number, initiator: 'back' | 'forward' | null = null) => {
      const cur = historyRef.current;
      if (newIdx < 0 || newIdx >= cur.entries.length || newIdx === cur.idx) {
        return;
      }
      const target = cur.entries[newIdx];
      const sameDoc = target.docId === cur.currentDocId;
      if (sameDoc) {
        sendCommand({
          type: 'sw-proxy-traverse-same-doc',
          url: target.url,
          state: target.state
        });
        updateHistory({ ...cur, idx: newIdx });
      } else {
        if (initiator) setLoadingButton(initiator);
        setIsLoading(true);
        sendCommand({ type: 'sw-proxy-load', url: target.url });
        updateHistory({ ...cur, idx: newIdx, currentDocId: target.docId });
      }
    },
    [sendCommand, updateHistory]
  );

  const canBack = history.idx > 0;
  const canForward = history.idx < history.entries.length - 1;

  React.useEffect(() => {
    const handler = (event: MessageEvent) => {
      // Only listen to messages from our specific iframe — we may be
      // mounted alongside other proxied iframes or proxy pop-ups, and
      // any of them could post.
      if (event.source !== iframeRef.current?.contentWindow) return;
      const data = event.data as
        | (ChildToParent & { [BRIDGE_FLAG]?: boolean })
        | undefined;
      if (!data || typeof data !== 'object' || data[BRIDGE_FLAG] !== true) {
        return;
      }
      switch (data.type) {
        case 'service-worker-not-supported':
        case 'service-worker-install-failed':
          // The iframe reports that the service worker couldn't be
          // installed, which means the proxy won't work at all. Surface
          // an error message instead of the loader spinner.
          setError('Service worker is not supported or failed to install.');
          break;
        case 'sw-proxy-navigate': {
          // Iframe is reporting a navigation intent.
          //   - sameDoc=true: iframe already replaceState'd. We just
          //     update the stack; no replay.
          //   - !sameDoc, letThrough=true: form submit — page is
          //     navigating itself. Append a new cross-doc entry.
          //   - !sameDoc, letThrough=false: cross-doc nav cancelled
          //     by iframe; we replay via sw-proxy-load.
          setCurrentUrl(data.url);
          const cur = historyRef.current;
          let { currentDocId, nextDocId } = cur;
          let entryDocId: number;

          if (data.sameDoc) {
            entryDocId = currentDocId;
          } else {
            entryDocId = nextDocId;
            nextDocId += 1;
            currentDocId = entryDocId;
          }

          let newEntries: HistoryEntry[];
          let newIdx: number;
          if (data.navigationType === 'replace') {
            // Replace updates the URL/state of the current entry. New
            // docId for cross-doc replace (location.replace) since
            // the document genuinely changed.
            if (cur.entries.length === 0) {
              newEntries = [
                { url: data.url, state: data.state, docId: entryDocId }
              ];
              newIdx = 0;
            } else {
              newEntries = cur.entries.slice();
              newEntries[cur.idx] = {
                url: data.url,
                state: data.state,
                docId: entryDocId
              };
              newIdx = cur.idx;
            }
          } else {
            // 'push' / 'traverse' from the iframe's POV: truncate any
            // forward history past the current entry and append.
            const trunc = cur.idx < 0 ? [] : cur.entries.slice(0, cur.idx + 1);
            trunc.push({
              url: data.url,
              state: data.state,
              docId: entryDocId
            });
            newEntries = trunc;
            newIdx = trunc.length - 1;
          }

          updateHistory({
            entries: newEntries,
            idx: newIdx,
            currentDocId,
            nextDocId
          });

          if (!data.sameDoc) {
            // Cross-doc nav (whether we replay it or the iframe
            // handles it itself via a form submit) → wait for the
            // matching `sw-proxy-loaded` before clearing the spinner.
            setIsLoading(true);
          }
          if (!data.sameDoc && !data.letThrough) {
            // Iframe needs us to perform the cross-doc load.
            sendCommand({ type: 'sw-proxy-load', url: data.url });
          }
          break;
        }
        case 'sw-proxy-loaded': {
          // Iframe document finished loading. Three reconciliation
          // cases:
          //   - First load ever: initialize stack.
          //   - Drift between requested URL and loaded URL: server
          //     redirect; update the current entry's URL.
          //   - Otherwise: synced.
          // After reconciliation, push the entry's stored state down
          // so `history.state` reflects what the parent has.
          setCurrentUrl(data.url);
          setIsLoading(false);
          setLoadingButton(null);
          const cur = historyRef.current;
          if (cur.entries.length === 0) {
            const docId = cur.nextDocId;
            updateHistory({
              entries: [{ url: data.url, state: null, docId }],
              idx: 0,
              currentDocId: docId,
              nextDocId: docId + 1
            });
            break;
          }
          const entry = cur.entries[cur.idx];
          if (entry && entry.url !== data.url) {
            const next = cur.entries.slice();
            next[cur.idx] = { ...entry, url: data.url };
            updateHistory({ ...cur, entries: next });
          }
          if (entry && entry.state != null) {
            sendCommand({
              type: 'sw-proxy-restore-state',
              state: entry.state
            });
          }
          break;
        }
        case 'sw-proxy-traverse-request': {
          // Page called history.back/forward/go. Same logic as the
          // toolbar buttons.
          const delta = Math.trunc(Number(data.delta) || 0);
          if (delta === 0) break;
          const cur = historyRef.current;
          const newIdx = Math.max(
            0,
            Math.min(cur.entries.length - 1, cur.idx + delta)
          );
          traverseTo(newIdx);
          break;
        }
        case 'sw-proxy-favicon':
          setFaviconUrl(data.href);
          break;
        case 'sw-proxy-title':
          setPageTitle(data.title);
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendCommand, updateHistory, traverseTo]);

  const handleBack = () => {
    if (!canBack) return;
    traverseTo(history.idx - 1, 'back');
  };
  const handleForward = () => {
    if (!canForward) return;
    traverseTo(history.idx + 1, 'forward');
  };
  const handleReload = () => {
    setLoadingButton('reload');
    setIsLoading(true);
    sendCommand({ type: 'sw-proxy-reload' });
  };
  // Right-click on Reload opens a small menu with the "Empty cache and
  // hard reload" action — modeled on Chromium's reload-button context
  // menu. We track screen coordinates rather than anchoring to the
  // button so the menu can sit just below the cursor.
  const [reloadMenu, setReloadMenu] = useState<{ x: number; y: number } | null>(
    null
  );
  React.useEffect(() => {
    if (!reloadMenu) return undefined;
    const close = () => setReloadMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // Close on any click anywhere, on Escape, and on blur so the menu
    // doesn't get stranded when focus leaves the window.
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('blur', close);
    };
  }, [reloadMenu]);
  const handleReloadContextMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setReloadMenu({ x: e.clientX, y: e.clientY });
  };
  const handleResetAndReload = () => {
    setReloadMenu(null);
    setLoadingButton('reload');
    setIsLoading(true);
    sendCommand({ type: 'sw-proxy-clear-site-data' });
  };
  const handleOpenInNewTab = () => {
    window.open(currentUrl, '_blank', 'noopener,noreferrer');
  };
  const handleCopyUrl = () => {
    navigator.clipboard?.writeText(currentUrl).catch(() => {
      /* clipboard may be unavailable in non-secure contexts; ignore */
    });
  };

  return (
    <div className="media_viewer_website">
      <div className="media_viewer_website__url_bar">
        <GlobalOutlined className="media_viewer_website__icon" />
        <div className="media_viewer_website__nav">
          <button
            type="button"
            className="media_viewer_website__icon_btn"
            tabIndex={-1}
            onClick={handleBack}
            disabled={!canBack}
            title="Back"
          >
            {isLoading && loadingButton === 'back' ? (
              <LoadingOutlined />
            ) : (
              <LeftOutlined />
            )}
          </button>
          <button
            type="button"
            className="media_viewer_website__icon_btn"
            tabIndex={-1}
            onClick={handleForward}
            disabled={!canForward}
            title="Forward"
          >
            {isLoading && loadingButton === 'forward' ? (
              <LoadingOutlined />
            ) : (
              <RightOutlined />
            )}
          </button>
          <button
            type="button"
            className="media_viewer_website__icon_btn"
            tabIndex={-1}
            onClick={handleReload}
            onContextMenu={handleReloadContextMenu}
            title="Reload (right-click for more)"
          >
            {isLoading &&
            (loadingButton === 'reload' || loadingButton === null) ? (
              <LoadingOutlined />
            ) : (
              <ReloadOutlined />
            )}
          </button>
          <button
            type="button"
            className="media_viewer_website__icon_btn"
            tabIndex={-1}
            onClick={handleOpenInNewTab}
            title="Open in new tab"
          >
            <ExportOutlined />
          </button>
        </div>
        <div className="media_viewer_website__url_field">
          {!!faviconUrl && (
            <img
              className="media_viewer_website__favicon"
              src={faviconUrl}
              alt=""
              // Favicons are fetched from the upstream origin directly
              // (the bridge sends an upstream URL). If the upstream
              // 4xxs the icon, hide the broken-image glyph silently.
              onError={() => setFaviconUrl(null)}
            />
          )}
          <span className="media_viewer_website__url_text" title={currentUrl}>
            {currentUrl}
          </span>
          <button
            type="button"
            className="media_viewer_website__icon_btn"
            tabIndex={-1}
            onClick={handleCopyUrl}
            title="Copy URL"
          >
            <CopyOutlined />
          </button>
        </div>
        <button
          type="button"
          className={`media_viewer_website__icon_btn${
            viewport === 'desktop'
              ? ' media_viewer_website__icon_btn--active'
              : ''
          }`}
          tabIndex={-1}
          onClick={() => setViewport('desktop')}
          title="Desktop"
        >
          <DesktopOutlined />
        </button>
        <button
          type="button"
          className={`media_viewer_website__icon_btn${
            viewport === 'tablet'
              ? ' media_viewer_website__icon_btn--active'
              : ''
          }`}
          tabIndex={-1}
          onClick={() => setViewport('tablet')}
          title="Tablet"
        >
          <TabletOutlined />
        </button>
        <button
          type="button"
          className={`media_viewer_website__icon_btn${
            viewport === 'mobile'
              ? ' media_viewer_website__icon_btn--active'
              : ''
          }`}
          tabIndex={-1}
          onClick={() => setViewport('mobile')}
          title="Mobile"
        >
          <MobileOutlined />
        </button>
      </div>
      <div
        className={`media_viewer_website__container media_viewer_website__container--${viewport}`}
      >
        {grant.ready && !error ? (
          <iframe
            ref={iframeRef}
            title={pageTitle}
            src={proxyUrl}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups-to-escape-sandbox allow-presentation"
            style={
              viewport === 'desktop'
                ? undefined
                : { width: VIEWPORT_SIZES[viewport] }
            }
          />
        ) : (
          <PlaybackPreparingLoader
            error={!!grant.error || !!error}
            message={
              grant.error
                ? `Could not authorise the preview: ${grant.error}`
                : error || 'Authorising preview...'
            }
          />
        )}
      </div>
      {reloadMenu && (
        <ul
          className="media_viewer_website__context_menu"
          style={{ left: reloadMenu.x, top: reloadMenu.y }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <li>
            <button
              type="button"
              className="media_viewer_website__context_menu_item"
              onClick={handleResetAndReload}
            >
              Empty Cache and Hard Reload
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export default WebsiteCard;
